import type { TransformerLoadingSummary, FeederLoadingSummary } from '../../types/engineering.ts'
import { LOADING_COLORS } from '../../types/engineering.ts'
import { sortFeedersWorstFirst } from '../../utils/feederSort.ts'
import { fmtKw, fmtPct } from '../../utils/format.ts'

interface CapacitySummaryProps {
  loading: TransformerLoadingSummary | undefined
  feeders: FeederLoadingSummary[] | undefined
  isLoading: boolean
  onSelectFeeder: (feederId: number) => void
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  )
}

export function CapacitySummary({ loading, feeders, isLoading, onSelectFeeder }: CapacitySummaryProps) {
  if (isLoading) {
    return <div className="text-sm text-gray-400 p-4">Loading capacity data...</div>
  }

  if (!loading) {
    return <div className="text-sm text-gray-400 p-4">No data available</div>
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Total Transformers" value={loading.total} color="#374151" />
        <StatCard label="Overloaded" value={loading.overloaded} color={LOADING_COLORS.overloaded} />
        <StatCard label="Heavy" value={loading.heavy} color={LOADING_COLORS.heavy} />
        <StatCard label="Normal" value={loading.normal} color={LOADING_COLORS.normal} />
        <StatCard label="Underloaded" value={loading.underloaded} color={LOADING_COLORS.underloaded} />
      </div>

      {loading.avg_loading_pct !== null && (
        <div className="text-sm text-gray-600">
          Average loading:{' '}
          <span className="font-semibold" title={`${loading.avg_loading_pct}%`}>
            {fmtPct(loading.avg_loading_pct)}
          </span>
          {loading.load_source_requested === 'measured'
            ? <> (measured/hybrid basis; +{loading.avg_demand_kw_used} kW per silent meter, model fallback where no AMI)</>
            : <> (at {loading.avg_demand_kw_used} kW/meter)</>}
        </div>
      )}

      {/* Feeder table */}
      {feeders && feeders.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Feeder Loading</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="pb-2 pr-3">Feeder</th>
                  <th className="pb-2 pr-3 text-right">Xfmrs</th>
                  <th className="pb-2 pr-3 text-right">Meters</th>
                  <th className="pb-2 pr-3 text-right">Total kVA</th>
                  <th className="pb-2 pr-3 text-right">
                    {loading?.load_source_requested === 'measured' ? 'Load kW' : 'Est. kW'}
                  </th>
                  <th className="pb-2 pr-3 text-right">Loading</th>
                  <th className="pb-2 text-right">Issues</th>
                </tr>
              </thead>
              <tbody>
                {/* B6: worst feeder first — the table is a triage surface. */}
                {sortFeedersWorstFirst(feeders).map(f => {
                  const pct = f.loading_pct
                  let barColor = LOADING_COLORS.normal
                  if (pct !== null) {
                    if (pct > 100) barColor = LOADING_COLORS.overloaded
                    else if (pct >= 70) barColor = LOADING_COLORS.heavy
                    else if (pct < 20) barColor = LOADING_COLORS.underloaded
                  }

                  return (
                    <tr
                      key={f.feeder_id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onSelectFeeder(f.feeder_id)}
                    >
                      <td className="py-2 pr-3">
                        <div className="font-medium text-gray-800">{f.feeder_name || `Feeder #${f.feeder_id}`}</div>
                        {f.substation_name && (
                          <div className="text-xs text-gray-400">{f.substation_name}</div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-600">{f.transformer_count}</td>
                      <td className="py-2 pr-3 text-right text-gray-600">{f.meter_count}</td>
                      <td className="py-2 pr-3 text-right text-gray-600" title={`${f.total_kva} kVA`}>
                        {fmtKw(f.total_kva)}
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-600" title={`${f.total_estimated_kw} kW`}>
                        {fmtKw(f.total_estimated_kw)}
                        {f.load_source_requested === 'measured' && (
                          <span className="block text-[10px] text-gray-400">
                            {f.measured_count} meas. / {f.model_fallback_count} est.
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${Math.min(pct ?? 0, 100)}%`,
                                backgroundColor: barColor,
                              }}
                            />
                          </div>
                          <span
                            className="font-medium"
                            style={{ color: barColor }}
                            title={pct !== null ? `${pct}%` : undefined}
                          >
                            {fmtPct(pct)}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        {f.overloaded_count > 0 && (
                          <span className="inline-block px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700 mr-1">
                            {f.overloaded_count} over
                          </span>
                        )}
                        {f.underloaded_count > 0 && (
                          <span className="inline-block px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                            {f.underloaded_count} under
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
